import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, History, RefreshCw } from 'lucide-react';
import { damagesAPI } from '../../api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useAppStore } from '../../stores/useAppStore';
import dayjs from '../../utils/dayjs';
import { printDamageDispatch } from '../../utils/pdf';

const DamageDispatchList = () => {
  const { formatPrice } = useCurrency();
  const { addNotification } = useAppStore();
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    damagesAPI.listDispatches()
      .then((res) => setDispatches(res.data))
      .catch(() => addNotification({ type: 'error', title: 'Damages', message: 'Failed to load shipments' }))
      .finally(() => setLoading(false));
  }, [addNotification]);

  const handleDownload = async (id) => {
    try {
      setDownloadingId(id);
      const res = await damagesAPI.getDispatch(id);
      await printDamageDispatch(res.data, formatPrice);
    } catch {
      addNotification({ type: 'error', title: 'PDF', message: 'Failed to create the PDF' });
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/damages" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-5 w-5" />
          <span>Damages</span>
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <History className="h-6 w-6 text-amber-600" />
          Sent to warehouse
        </h1>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {dispatches.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-gray-500">
            {loading ? 'Loading...' : 'No damages have been sent to the warehouse yet.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Shipment</th>
                  <th className="px-4 py-2 text-left font-medium">Sent</th>
                  <th className="px-4 py-2 text-left font-medium">Damages from</th>
                  <th className="px-4 py-2 text-right font-medium">Pieces</th>
                  <th className="px-4 py-2 text-right font-medium">Value</th>
                  <th className="px-4 py-2 text-left font-medium">Sent by</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {dispatches.map((x) => (
                  <tr key={x.ID}>
                    <td className="px-4 py-2">
                      <Link to={`/damages/dispatches/${x.ID}`} className="font-mono font-semibold text-blue-700 hover:underline">
                        {x.DispatchNo}
                      </Link>
                      <div className="text-xs text-gray-500">{x.LineCount} lines</div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">{dayjs(x.DispatchDate).format('DD MMM YYYY, h:mm A')}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                      {x.PeriodStart ? dayjs(x.PeriodStart).format('DD MMM YYYY') : 'First shipment'}
                    </td>
                    <td className="px-4 py-2 text-right font-semibold">{x.TotalQuantity}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">{formatPrice(x.TotalValue)}</td>
                    <td className="px-4 py-2">{x.CreatedByName || '-'}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => handleDownload(x.ID)}
                        disabled={downloadingId === x.ID}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-300 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        title="Download the PDF again"
                      >
                        {downloadingId === x.ID ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                        PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DamageDispatchList;
